/**
 * AuthLayoutShell — coquille commune aux 4 espaces authentifiés
 * (etudiant, entreprise, responsable, admin).
 *
 * Header PASSAGE :
 *   - Logo "C." tomate + wordmark
 *   - Badge rôle coloré (passé en prop)
 *   - Email + bouton "Se déconnecter"
 *
 * Body :
 *   - Sidebar à gauche (sticky, scrollable)
 *   - Main à droite (passeCard ou plain selon préférence)
 *
 * Footer minimal.
 *
 * Stratégie : chaque Layout (EtudiantLayout, etc.) appelle ce shell avec
 * son propre nav + son propre badge. Aucune duplication d'header.
 */
import type { ReactNode } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { APP_NAME } from '@lecomptoir/shared';
import { useCurrentUser } from '@/lib/auth';
import { apiFetch } from '@/lib/api';
import { Sticker, type StickerColor } from '@/components/passage/Sticker';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';

export interface AuthLayoutShellProps {
  /** Lien du logo (chemin racine de l'espace). */
  homePath: string;
  /** Texte du badge rôle (ex: "Candidat", "Entreprise"). */
  roleLabel: string;
  /** Couleur du badge rôle. */
  roleColor: StickerColor;
  /** Sticker(s) additionnel(s) après le badge rôle (ex: nom d'établissement). */
  extraHeader?: ReactNode;
  /** Navigation latérale (composer avec <SidebarLink>). */
  sidebar: ReactNode;
  /** ARIA label de la sidebar. */
  navAriaLabel: string;
  /** Largeur de la sidebar (Tailwind grid template). */
  sidebarWidth?: string;
  /** Texte du footer. */
  footerText?: string;
}

export function AuthLayoutShell({
  homePath,
  roleLabel,
  roleColor,
  extraHeader,
  sidebar,
  navAriaLabel,
  sidebarWidth = '220px',
  footerText,
}: AuthLayoutShellProps) {
  const { data: user } = useCurrentUser();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const logout = useMutation({
    mutationFn: () => apiFetch('/auth/logout', { method: 'POST' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['auth', 'me'] });
      navigate('/connexion');
    },
  });

  return (
    <div className="flex min-h-screen flex-col bg-white text-passage-ink">
      <ImpersonationBanner />
      <a href="#main" className="skip-link">
        Aller au contenu principal
      </a>

      <header className="border-b-2 border-passage-ink bg-white">
        <div className="container flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to={homePath} className="flex items-center gap-2.5">
              <span className="grid h-9 w-9 place-items-center rounded-[10px] border-2 border-passage-ink bg-passage-tomate text-[17px] font-black text-white shadow-passage-sm">
                C
              </span>
              <span className="text-[22px] font-black -tracking-[0.02em] text-passage-ink">
                {APP_NAME}<span className="text-passage-tomate">.</span>
              </span>
            </Link>
            <Sticker color={roleColor} rotate={-3} shadow="xs">
              ● {roleLabel}
            </Sticker>
            {extraHeader}
          </div>

          <div className="flex items-center gap-3 text-sm">
            {user && (
              <span className="hidden text-passage-ink-soft sm:inline">{user.email}</span>
            )}
            <button
              type="button"
              onClick={() => logout.mutate()}
              className="rounded-xl border-2 border-passage-ink bg-white px-3 py-1.5 text-sm font-bold text-passage-ink shadow-passage-sm transition hover:translate-y-[2px] hover:shadow-passage-xs"
            >
              Se déconnecter
            </button>
          </div>
        </div>
      </header>

      <div className="container flex-1 py-8">
        <div
          className="grid gap-6"
          style={{ gridTemplateColumns: `${sidebarWidth} 1fr` }}
        >
          <nav aria-label={navAriaLabel} className="space-y-1">
            {sidebar}
          </nav>

          <main
            id="main"
            tabIndex={-1}
            className="rounded-2xl border-2 border-passage-ink bg-white p-6 shadow-passage"
          >
            <Outlet />
          </main>
        </div>
      </div>

      <footer className="border-t-2 border-passage-ink bg-passage-cream py-4 text-center text-xs font-medium text-passage-ink-soft">
        {footerText ?? `© ${new Date().getFullYear()} Stelvyo`}
      </footer>
    </div>
  );
}
